'use client';

import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { pickChrome, type SiteChromeKey } from '../../lib/siteChromeCopy';

export type SiteFormStatus = 'idle' | 'sending' | 'success' | 'error' | 'preview';

const DEFAULT_STATUS_CHROME: Record<Exclude<SiteFormStatus, 'idle'>, SiteChromeKey> = {
  sending: 'formStatusSending',
  success: 'formStatusSuccess',
  error: 'formStatusError',
  preview: 'formStatusPreview',
};

const TURNSTILE_TOKEN_WAIT_MS = 12_000;

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string;
          size?: string;
          appearance?: 'always' | 'execute' | 'interaction-only';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        },
      ) => string;
      getResponse: (widgetId: string) => string;
      reset: (widgetId: string) => void;
    };
  }
}

function resolveBrowserTurnstileSiteKey(): string | null {
  if (typeof document === 'undefined') return null;
  const fromBody = document.body?.getAttribute('data-turnstile-site-key')?.trim();
  if (fromBody) return fromBody;
  const fromEnv =
    typeof process !== 'undefined'
      ? process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY?.trim()
      : undefined;
  return fromEnv || null;
}

function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-ws-turnstile]');
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile')));
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.dataset.wsTurnstile = '1';
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile'));
    document.head.appendChild(script);
  });
}

function waitForToken(
  read: () => string,
  timeoutMs: number,
): Promise<string> {
  const immediate = read();
  if (immediate) return Promise.resolve(immediate);
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      const token = read();
      if (token) {
        window.clearInterval(timer);
        resolve(token);
        return;
      }
      if (Date.now() - started >= timeoutMs) {
        window.clearInterval(timer);
        reject(new Error('turnstile token missing'));
      }
    }, 200);
  });
}

export function SiteLeadForm({
  locale,
  formKey,
  editable,
  className,
  children,
  statusChrome,
}: {
  locale: string;
  formKey: string;
  editable?: boolean;
  className: string;
  children: ReactNode;
  /** Override status chrome keys (e.g. reservation-specific success copy). */
  statusChrome?: Partial<Record<Exclude<SiteFormStatus, 'idle'>, SiteChromeKey>>;
}) {
  const [status, setStatus] = useState<SiteFormStatus>('idle');
  const chromeKeys = { ...DEFAULT_STATUS_CHROME, ...statusChrome };
  const turnstileHostRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const tokenRef = useRef<string>('');
  const siteKey = !editable ? resolveBrowserTurnstileSiteKey() : null;

  useEffect(() => {
    if (!siteKey || editable || !turnstileHostRef.current) return;
    let cancelled = false;
    void loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileHostRef.current || !window.turnstile) return;
        if (widgetIdRef.current) return;
        widgetIdRef.current = window.turnstile.render(turnstileHostRef.current, {
          sitekey: siteKey,
          size: 'flexible',
          appearance: 'interaction-only',
          callback: (token) => {
            tokenRef.current = token;
          },
          'expired-callback': () => {
            tokenRef.current = '';
          },
          'error-callback': () => {
            tokenRef.current = '';
          },
        });
      })
      .catch(() => {
        /* submit will fail closed if server requires Turnstile */
      });
    return () => {
      cancelled = true;
    };
  }, [editable, siteKey]);

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;

    if (editable) {
      setStatus('preview');
      return;
    }

    if (!form.reportValidity()) {
      return;
    }

    setStatus('sending');
    const data = new FormData(form);
    const payload: Record<string, unknown> = {};
    data.forEach((value, key) => {
      if (key === 'consent' || key === 'cf-turnstile-response') {
        if (key === 'consent') payload.consent = true;
        return;
      }
      payload[key] = typeof value === 'string' ? value : String(value);
    });

    let turnstileToken: string | undefined;
    try {
      if (siteKey) {
        turnstileToken = await waitForToken(() => {
          if (tokenRef.current) return tokenRef.current;
          if (widgetIdRef.current && window.turnstile) {
            try {
              return window.turnstile.getResponse(widgetIdRef.current) || '';
            } catch {
              return '';
            }
          }
          return '';
        }, TURNSTILE_TOKEN_WAIT_MS);
      }

      const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
      const response = await fetch(`/api/public/leads/${encodeURIComponent(hostname)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          formKey,
          payload,
          locale,
          sourcePageSlug:
            typeof window !== 'undefined'
              ? window.location.pathname.replace(/^\//, '') || 'index'
              : undefined,
          turnstileToken,
        }),
      });
      if (!response.ok) {
        throw new Error('submit failed');
      }
      setStatus('success');
      form.reset();
      tokenRef.current = '';
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    } catch {
      setStatus('error');
      tokenRef.current = '';
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
      }
    }
  }

  const statusMessage =
    status === 'idle' ? null : pickChrome(chromeKeys[status], locale);

  return (
    <form className={className} action="#" method="post" noValidate onSubmit={onSubmit}>
      {children}
      {siteKey ? (
        <div ref={turnstileHostRef} className="ws-form__turnstile" data-ws-turnstile-host="1" />
      ) : null}
      {statusMessage ? (
        <p
          className={['ws-form__status', `ws-form__status--${status}`].join(' ')}
          role="status"
          aria-live="polite"
        >
          {statusMessage}
        </p>
      ) : (
        <p className="ws-form__status ws-form__status--idle" role="status" aria-live="polite" />
      )}
    </form>
  );
}
